import {
  ADMIN_REFUND_REASONS,
  type AdminOrderDetailDto,
  type AdminRefundReason,
} from '@carcheck/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useEffect, useMemo, type ReactElement } from 'react';
import {
  Controller,
  useForm,
  type Control,
  type FieldErrors,
  type UseFormRegister,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { RedactedField } from '../../../components/pii';
import { formatCurrency } from '../../../utils/format';

/**
 * Order-initiated "Оформить возврат" dialog (AUT-129 / ADMIN-17). Collects
 * the refund amount + reason via RHF + Zod, then resolves them to the
 * parent (`PaymentTab`), which routes the POST through the reactive
 * conditional step-up (`useIssueRefund`).
 *
 * **Reason = `admin_manual` only (architect guardrail #2):** the operator-
 * selectable reason for a manual refund is `admin_manual` — the only
 * operator semantics. The other `ADMIN_REFUND_REASONS` are listener-emitted
 * (`customer_cancel_*` / `dispute_*` / `inspector_cancel`), so the select
 * offers a single option (default + only). Sourced from the shared enum,
 * never hand-typed (the mockup's `dispute_customer` is INVALID → would 422).
 *
 * **Client-side over-refund guard (architect guardrail #4):** the dialog
 * shows "доступно к возврату" = `payment.amount − payment.refundedTotal`
 * and blocks an amount above it. The backend stays the authority too (a
 * `refund_amount_exceeds` rejection is mapped + toasted by `useIssueRefund`).
 *
 * The mockup's free-text "Комментарий" field is intentionally DROPPED
 * (build-to-contract): the refund body is `{amount, reason, recipientId,
 * idempotencyKey}` with no note, and the `@Audit` row records only
 * amount/reason/recipientId — a comment has no wire home and would falsely
 * promise "попадёт в журнал аудита". The `reason` is the audited
 * justification.
 */

/** The only operator-selectable reason for a manual refund (architect #2). */
const OPERATOR_REASON: AdminRefundReason = 'admin_manual';

/** Guard: the operator reason must be a member of the shared enum. */
const OPERATOR_REASONS = ADMIN_REFUND_REASONS.filter((r) => r === OPERATOR_REASON);

export interface IssueRefundResult {
  amount: string;
  reason: AdminRefundReason;
}

export interface IssueRefundDialogProps {
  open: boolean;
  loading?: boolean;
  order: AdminOrderDetailDto;
  onConfirm: (input: IssueRefundResult) => void;
  onCancel: () => void;
}

/** Read-only Order + Recipient (PII-gated) header fields — extracted to
 * keep the dialog function under the line cap. */
const ReadOnlyFields = ({ order }: { order: AdminOrderDetailDto }): ReactElement => {
  const { t } = useTranslation();
  return (
    <>
      <Box>
        <Typography variant="caption" color="text.secondary">
          {t('orders.issueRefundDialog.orderLabel')}
        </Typography>
        <Typography variant="body2" fontFamily="monospace">
          {order.orderNumber}
        </Typography>
      </Box>
      <Box>
        <Typography variant="caption" color="text.secondary">
          {t('orders.issueRefundDialog.recipientLabel')}
        </Typography>
        <Typography variant="body2" component="div">
          {order.customer.name ? (
            <RedactedField
              perm="view_pii"
              value={order.customer.name}
              redacted={order.customer.name}
              {...(order.piiRevealed ? { copyable: true } : {})}
            />
          ) : (
            t('orders.detail.noValue')
          )}
        </Typography>
      </Box>
    </>
  );
};

/** Build the RHF + Zod schema bound to the live available-to-refund ceiling. */
const buildSchema = (available: number) =>
  z.object({
    amount: z
      .string()
      .trim()
      .min(1, 'orders.issueRefundDialog.amountRequired')
      .refine((v) => /^\d+(\.\d{1,2})?$/.test(v), 'orders.issueRefundDialog.amountFormat')
      .refine((v) => Number(v) > 0, 'orders.issueRefundDialog.amountPositive')
      // Backend stays authority — this is a UX pre-block (architect #4).
      .refine((v) => Number(v) <= available + 1e-9, 'orders.issueRefundDialog.amountTooHigh'),
    reason: z.enum(ADMIN_REFUND_REASONS),
  });

type RefundForm = { amount: string; reason: AdminRefundReason };

/** The amount + reason form fields. Extracted to keep the dialog function
 * under the line cap. */
const RefundFormFields = ({
  control,
  register,
  errors,
  loading,
  amountHint,
}: {
  control: Control<RefundForm>;
  register: UseFormRegister<RefundForm>;
  errors: FieldErrors<RefundForm>;
  loading: boolean;
  amountHint: string;
}): ReactElement => {
  const { t } = useTranslation();
  return (
    <>
      <TextField
        {...register('amount')}
        fullWidth
        size="small"
        disabled={loading}
        label={t('orders.issueRefundDialog.amountLabel')}
        error={Boolean(errors.amount)}
        helperText={errors.amount ? t(errors.amount.message ?? '') : amountHint}
        slotProps={{ htmlInput: { inputMode: 'decimal' } }}
      />
      <Controller
        name="reason"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            select
            fullWidth
            size="small"
            disabled={loading}
            label={t('orders.issueRefundDialog.reasonLabel')}
          >
            {OPERATOR_REASONS.map((reason) => (
              <MenuItem key={reason} value={reason}>
                {t(`refunds.reason.${reason}`)}
              </MenuItem>
            ))}
          </TextField>
        )}
      />
    </>
  );
};

export const IssueRefundDialog = ({
  open,
  loading = false,
  order,
  onConfirm,
  onCancel,
}: IssueRefundDialogProps): ReactElement | null => {
  const { t } = useTranslation();
  const payment = order.payment;

  const paid = payment ? Number(payment.amount) : 0;
  const refunded = payment ? Number(payment.refundedTotal) : 0;
  const available = Math.max(paid - refunded, 0);
  const defaultAmount = available.toFixed(2);

  const schema = useMemo(() => buildSchema(available), [available]);

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors, isValid },
  } = useForm<RefundForm>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    defaultValues: { amount: defaultAmount, reason: OPERATOR_REASON },
  });

  useEffect(() => {
    if (open) reset({ amount: defaultAmount, reason: OPERATOR_REASON });
  }, [open, defaultAmount, reset]);

  // Defensive: a refund cannot be issued without a payment row.
  if (!payment) return null;

  const submit = (values: RefundForm): void => {
    if (loading) return;
    onConfirm({ amount: values.amount.trim(), reason: values.reason });
  };

  const amountHint = t('orders.issueRefundDialog.amountHint', {
    paid: formatCurrency(paid, payment.currency),
    available: formatCurrency(available, payment.currency),
  });

  return (
    <Dialog open={open} onClose={loading ? undefined : onCancel} maxWidth="xs" fullWidth>
      <DialogTitle>
        {t('orders.issueRefundDialog.title')}
        <Typography variant="body2" color="text.secondary">
          {t('orders.issueRefundDialog.subtitle')}
        </Typography>
      </DialogTitle>
      <form onSubmit={handleSubmit(submit)}>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <ReadOnlyFields order={order} />
          <RefundFormFields
            control={control}
            register={register}
            errors={errors}
            loading={loading}
            amountHint={amountHint}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onCancel} disabled={loading}>
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="error"
            disabled={loading || !isValid}
            loading={loading}
          >
            {t('orders.issueRefundDialog.confirm')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
